import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, FileDown, CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi, fmtDate, fmtDur } from "@/lib/api";
import { Verdict } from "@/components/Bits";
import Transcript, { RecordingButton } from "@/components/Transcript";

const RATING = { good: "text-emerald-500 bg-emerald-500/10", adequate: "text-amber-500 bg-amber-500/10", poor: "text-rose-500 bg-rose-500/10" };
const LABELS = { clarity: "Clarity", consistency: "Consistency", truthfulness_credibility: "Credibility", completeness: "Completeness", composure_politeness: "Composure" };

const List = ({ title, items, icon: Icon, color, testid }) => (
  <div className="bg-slate-900 border border-slate-800 p-6" data-testid={testid}>
    <p className={`font-mono text-[10px] tracking-[0.2em] uppercase mb-4 flex items-center gap-2 ${color}`}><Icon className="w-3 h-3" /> {title}</p>
    {items?.length ? <ul className="space-y-2 text-sm text-slate-300">{items.map((x, i) => <li key={i}>— {x}</li>)}</ul> : <p className="text-sm text-slate-600">None</p>}
  </div>
);

function Evaluation({ s }) {
  const ev = s.evaluation;
  const pass = ev.verdict === "PASS";
  return (
    <div className="space-y-6">
      <div className={`border p-8 lg:p-10 grid md:grid-cols-12 gap-8 ${pass ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid="verdict-panel">
        <div className="md:col-span-4">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">AI verdict</p>
          <p className={`font-cabinet font-extrabold text-6xl mt-2 ${pass ? "text-emerald-500" : "text-rose-500"}`} data-testid="verdict-text">{pass ? "PASSED" : "FAILED"}</p>
          <p className="font-cabinet font-bold text-white text-3xl mt-4" data-testid="overall-score">{ev.overall_score}<span className="text-slate-500 text-lg">/100</span></p>
        </div>
        <div className="md:col-span-8">
          <p className="text-slate-300 leading-relaxed" data-testid="evaluation-summary">{ev.summary}</p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            {Object.entries(ev.category_scores || {}).map(([k, v]) => (
              <div key={k} data-testid={`category-score-${k}`}>
                <p className="font-mono text-[10px] text-slate-500 uppercase">{LABELS[k] || k}</p>
                <div className="h-1.5 bg-slate-800 mt-2"><div className="h-full bg-blue-500" style={{ width: `${v}%` }} /></div>
                <p className="font-mono text-xs text-white mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <List title="Strengths" items={ev.strengths} icon={CheckCircle2} color="text-emerald-500" testid="eval-strengths" />
        <List title="Improvements" items={ev.improvements} icon={AlertTriangle} color="text-amber-500" testid="eval-improvements" />
        <List title="Red flags" items={ev.red_flags} icon={XCircle} color="text-rose-500" testid="eval-red-flags" />
      </div>
      <div className="border border-slate-800 divide-y divide-slate-800" data-testid="per-answer-list">
        {(ev.per_answer || []).map((a, i) => (
          <div key={i} className="p-6 grid lg:grid-cols-12 gap-4 bg-slate-900" data-testid={`per-answer-${i}`}>
            <div className="lg:col-span-5">
              <p className="font-mono text-[10px] text-slate-500 mb-1">Q{i + 1} · OFFICER</p>
              <p className="text-sm text-slate-300">{a.question}</p>
              <p className="font-mono text-[10px] text-blue-400 mt-3 mb-1 flex items-center gap-2">YOUR ANSWER {s.turns?.[a.turn_index]?.audio_path && <RecordingButton sessionId={s.id} idx={a.turn_index} />}</p>
              <p className="text-sm text-white">{a.answer}</p>
            </div>
            <div className="lg:col-span-7">
              <span className={`font-mono text-[10px] tracking-wider px-2 py-1 uppercase ${RATING[a.rating] || RATING.adequate}`}>{a.rating}</span>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{a.feedback}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SessionResult({ admin }) {
  const { id } = useParams();
  const api = useApi();
  const [s, setS] = useState(null);
  const load = useCallback(() => api("get", `/sessions/${id}`).then(setS), [api, id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (s?.evaluation_status !== "pending") return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [s, load]);

  const reEval = async () => {
    await api("post", `/admin/sessions/${id}/evaluate`);
    toast.success("Re-evaluation started");
    load();
  };

  const downloadPdf = async () => {
    try {
      const blob = await api("get", `/admin/sessions/${id}/report`, undefined, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assessment_${(s.user_name || "traveller").replace(/\W+/g, "_")}_${s.started_at.slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the PDF"); }
  };

  if (!s) return <div className="font-mono text-xs text-slate-500" data-testid="result-loading">LOADING...</div>;
  return (
    <div className="fade-up" data-testid="session-result-page">
      <Link to={admin ? "/admin/sessions" : "/dashboard"} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors" data-testid="result-back-link">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400 mb-3">Assessment record</p>
          <h1 className="font-cabinet font-extrabold text-white text-4xl sm:text-5xl tracking-tight">{s.destination}</h1>
          <p className="text-slate-400 mt-3 text-sm">
            {admin && <span className="text-white" data-testid="result-user">{s.user_name} · <span className="font-mono">{s.user_email}</span> · </span>}
            {s.team_role_label}
          </p>
          <p className="font-mono text-xs text-slate-500 mt-2" data-testid="result-timestamps">Started {fmtDate(s.started_at)} · Ended {fmtDate(s.ended_at)} · {fmtDur(s.duration_seconds)}</p>
        </div>
        <div className="flex items-center gap-3">
          <Verdict session={s} testid="result-verdict-badge" />
          {admin && (
            <button onClick={downloadPdf} data-testid="download-pdf-btn" className="inline-flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 transition-colors">
              <FileDown className="w-4 h-4" /> Download PDF
            </button>
          )}
          {admin && s.status === "completed" && (
            <button onClick={reEval} disabled={s.evaluation_status === "pending"} data-testid="re-evaluate-btn" className="inline-flex items-center gap-2 text-sm border border-slate-700 hover:border-slate-500 text-slate-200 px-4 py-2 disabled:opacity-50 transition-colors">
              <RefreshCw className="w-4 h-4" /> Re-run AI evaluation
            </button>
          )}
        </div>
      </div>

      {!admin ? (
        <>
          <div className="border border-slate-800 bg-slate-900 p-8 mb-6" data-testid="submitted-note">
            <p className="text-slate-300">{s.status === "completed" ? "Your interview has been submitted. The assessment is shared with the super admin only." : "This interview is still in progress."}</p>
          </div>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">Full transcript ({s.turns.length})</p>
          <div className="bg-slate-900 border border-slate-800"><Transcript turns={s.turns} sessionId={s.id} /></div>
        </>
      ) : (
      <Tabs defaultValue="evaluation">
        <TabsList className="bg-slate-900 border border-slate-800 rounded-none mb-6">
          <TabsTrigger value="evaluation" data-testid="tab-evaluation" className="rounded-none">Evaluation</TabsTrigger>
          <TabsTrigger value="transcript" data-testid="tab-transcript" className="rounded-none">Full transcript ({s.turns.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="evaluation">
          {s.evaluation_status === "done" && s.evaluation ? <Evaluation s={s} /> : (
            <div className="border border-slate-800 bg-slate-900 p-12 text-center" data-testid="evaluation-pending">
              {s.evaluation_status === "pending" && <><Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" /><p className="mt-4 text-slate-300">The AI assessor is reviewing your interview. This usually takes under a minute.</p></>}
              {s.evaluation_status === "failed" && <p className="text-rose-400">Evaluation failed. {admin ? "Use “Re-run AI evaluation”." : "Please ask an administrator to re-run it."}</p>}
              {s.status === "in_progress" && <p className="text-slate-400">This interview is still in progress.</p>}
            </div>
          )}
        </TabsContent>
        <TabsContent value="transcript">
          <div className="bg-slate-900 border border-slate-800"><Transcript turns={s.turns} sessionId={s.id} /></div>
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
